import React, { useMemo } from "react";
import Highcharts from "../../config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";
import { useAppSelector } from "@store/index";
import { donutChartConfigSelector } from "@store/slices/donut-chart-config-slice";
import { useDonutChartOptions } from "@hooks/use-donut-chart-options";
import { useDonutChartLegend } from "@hooks/use-donut-chart-legend";
import { ChartLegendSidebar } from "./ChartLegendSidebar";
import { getTimePeriodNominalRepresentation } from "@/utils/time-period-display";
import { resolveTimePeriodInWindow } from "@/utils/time-period-utils";
import type { TimePeriod } from "@store/slices/crosstab-config-slice";

// Highcharts modules (Exporting, Accessibility) are now initialized globally in highcharts-config.ts
// This provides hamburger menu functionality across all chart components

interface ConfigurableDonutChartProps {
  dashboardDateOverride?: { startDate: string; endDate: string };
}

const ConfigurableDonutChart: React.FC<ConfigurableDonutChartProps> = ({
  dashboardDateOverride,
}) => {
  const { currentConfig } = useAppSelector(donutChartConfigSelector);

  const { visiblePoints, handleLegendClick, chartCallback } =
    useDonutChartLegend();

  // Calculate dashboard filter note — mirrors BarChart's Date Filter behavior.
  const dashboardFilterNote = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate) {
      return null;
    }
    const timePeriod = currentConfig?.timePeriodSelection?.timePeriod as
      | TimePeriod
      | undefined;
    if (
      !timePeriod ||
      timePeriod === "all_time" ||
      timePeriod === "custom_period"
    ) {
      return `Time Period (filtered): ${dashboardDateOverride.startDate} – ${dashboardDateOverride.endDate}`;
    }
    const resolved = resolveTimePeriodInWindow(
      timePeriod,
      dashboardDateOverride.startDate,
      dashboardDateOverride.endDate,
    );
    if (!resolved) {
      const unit = timePeriod.includes("month")
        ? "month"
        : timePeriod.includes("semester")
          ? "semester"
          : "quarter";
      return `No complete ${unit} available within the selected date range.`;
    }
    const label = getTimePeriodNominalRepresentation(
      timePeriod,
      resolved.startDate,
      resolved.endDate,
    );
    return label ? `Time Period (filtered): ${label}` : null;
  }, [dashboardDateOverride, currentConfig?.timePeriodSelection?.timePeriod]);

  // Apply grouping and sorting to chart data
  const chartData = useMemo(() => {
    const manualColors = currentConfig.manualColors;
    const useManualColors = currentConfig.colorPalette === "Custom (per-slice)";
    // Resolve manual color overrides first so a group slice that inherits its
    // color from its first member picks up that member's override too.
    const rawData = (currentConfig.series[0]?.data || []).map((item) =>
      useManualColors && manualColors[item.name]
        ? { ...item, color: manualColors[item.name] }
        : item,
    );
    let processedData = rawData.map((item) => ({ ...item }));

    // Apply grouping logic
    if (currentConfig.groups && currentConfig.groups.length > 0) {
      const groupedData = [...processedData];
      const usedSliceNames = new Set<string>();

      // Process each group
      currentConfig.groups.forEach((group) => {
        if (group.sliceNames.length >= 2 && group.name.trim()) {
          // Calculate group total
          const groupTotal = group.sliceNames.reduce((sum, sliceName) => {
            const slice = rawData.find((s) => s.name === sliceName);
            return sum + (slice?.y || 0);
          }, 0);

          // Group N (Slice N) is the sum of member slices' unweighted respondent counts.
          const groupCellN = group.sliceNames.reduce(
            (sum: number | null, sliceName) => {
              const slice = rawData.find((s) => s.name === sliceName);
              if (slice?.cellN == null) return sum;
              return (sum ?? 0) + slice.cellN;
            },
            null as number | null,
          );

          if (group.showAsSlice) {
            // Replace individual slices with group slice
            group.sliceNames.forEach((sliceName) => {
              usedSliceNames.add(sliceName);
              const index = groupedData.findIndex((s) => s.name === sliceName);
              if (index !== -1) {
                groupedData.splice(index, 1);
              }
            });

            // Add group slice
            groupedData.push({
              name: group.name,
              y: groupTotal,
              color: group.sliceNames[0]
                ? rawData.find((s) => s.name === group.sliceNames[0])?.color
                : "#57bfe7",
              isGroup: true,
              groupId: group.id,
              cellN: groupCellN,
            });
          } else {
            // Keep individual slices, just mark them as part of a group
            group.sliceNames.forEach((sliceName) => {
              const slice = groupedData.find((s) => s.name === sliceName);
              if (slice) {
                slice.groupId = group.id;
                slice.groupName = group.name;
              }
            });
          }
        }
      });

      processedData = groupedData;
    }

    // Apply percentage-based sorting
    let sorted = processedData;
    if (currentConfig.dataSorting === "ascending") {
      sorted = [...processedData].sort((a, b) => a.y - b.y);
    } else if (currentConfig.dataSorting === "descending") {
      sorted = [...processedData].sort((a, b) => b.y - a.y);
    }
    // "none" (Original survey order) keeps processedData order as-is

    // Custom Order supersedes pinning; otherwise Pinned Start/End apply on
    // top of whatever order was established above.
    const sc = currentConfig.sliceCustomization;
    if (currentConfig.dataSorting === "custom" && sc.customOrder.length > 0) {
      const orderIndex = new Map(sc.customOrder.map((n, i) => [n, i]));
      sorted = [...processedData].sort((a, b) => {
        const ai = orderIndex.has(a.name)
          ? orderIndex.get(a.name)!
          : processedData.length;
        const bi = orderIndex.has(b.name)
          ? orderIndex.get(b.name)!
          : processedData.length;
        return ai - bi;
      });
    } else if (sc.pinnedStart || sc.pinnedEnd) {
      const startItem = sc.pinnedStart
        ? sorted.find((s) => s.name === sc.pinnedStart)
        : null;
      const endItem = sc.pinnedEnd
        ? sorted.find((s) => s.name === sc.pinnedEnd)
        : null;
      const middle = sorted.filter(
        (s) => s.name !== sc.pinnedStart && s.name !== sc.pinnedEnd,
      );
      sorted = [
        ...(startItem ? [startItem] : []),
        ...middle,
        ...(endItem ? [endItem] : []),
      ];
    }

    return sorted;
  }, [
    currentConfig.series,
    currentConfig.dataSorting,
    currentConfig.groups,
    currentConfig.manualColors,
    currentConfig.colorPalette,
    currentConfig.sliceCustomization,
  ]);

  // Use the shared donut chart options hook
  const { options, filteredChartData } = useDonutChartOptions({
    config: currentConfig,
    chartData,
    isConfigPreview: true, // This is the config preview component
  });

  // Legend is built from the same slice set actually rendered on the pie
  // (filteredChartData) so a slice hidden by the sub-1% filter doesn't leave
  // a dangling legend row with nothing on the chart to click.
  const legendItems = useMemo(() => {
    const labelOverrides = currentConfig.sliceCustomization.labelOverrides;
    return filteredChartData.map((item) => {
      const label = labelOverrides[item.name] ?? item.name;
      return {
        ...item,
        titleText: label,
        displayName:
          currentConfig.truncateCategoryText && label.length > 40
            ? label.substring(0, 40) + "..."
            : label,
        secondaryText:
          currentConfig.showSliceN && item.cellN != null
            ? `N: ${item.cellN.toLocaleString()}`
            : undefined,
      };
    });
  }, [
    filteredChartData,
    currentConfig.showSliceN,
    currentConfig.truncateCategoryText,
    currentConfig.sliceCustomization,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 400,
        position: "relative",
        display: "flex",
      }}
    >
      {currentConfig.legendEnabled && (
        <ChartLegendSidebar
          legendItems={legendItems}
          visibleItems={visiblePoints}
          onLegendClick={(indexOrName) =>
            handleLegendClick(indexOrName as number)
          }
          useNameBasedVisibility={false}
        />
      )}

      <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
          callback={chartCallback}
          label="Configurable Donut Chart"
        />
      </div>

      {dashboardFilterNote && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            p: 1,
            px: 2.5,
            backgroundColor: "#f9fafb",
            borderRadius: 1,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="caption" sx={{ color: "#1976d2" }} fontWeight={600}>
            {dashboardFilterNote}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default ConfigurableDonutChart;
